import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Users, UserPlus, Copy, Trash2, Loader2, AlertTriangle, Share2,
  CalendarPlus, Search, Check, X, UserCheck, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";

const FriendsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteLink, setInviteLink] = useState(null);
  const [creatingInvite, setCreatingInvite] = useState(false);

  // Discovery
  const [searchQuery, setSearchQuery] = useState("");
  const [discoveredUsers, setDiscoveredUsers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(null);

  // Friend requests received
  const [friendRequests, setFriendRequests] = useState([]);
  const [processingRequest, setProcessingRequest] = useState(null);

  useEffect(() => {
    fetchFriends();
    fetchFriendRequests();
  }, [token]);

  const fetchFriends = async () => {
    try {
      const res = await axios.get(`${API}/friends`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFriends(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFriendRequests = async () => {
    try {
      const res = await axios.get(`${API}/friend-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFriendRequests(res.data);
    } catch { /* ignore */ }
  };

  const searchUsers = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) {
      setDiscoveredUsers([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/users/discover?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDiscoveredUsers(res.data);
    } catch { /* ignore */ } finally {
      setSearchLoading(false);
    }
  };

  const loadAllUsers = async () => {
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/users/discover?q=`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDiscoveredUsers(res.data);
    } catch { /* ignore */ } finally {
      setSearchLoading(false);
    }
  };

  const sendFriendRequest = async (userId) => {
    setSendingRequest(userId);
    try {
      const res = await axios.post(`${API}/friend-requests/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      if (res.data.status === "accepted") {
        fetchFriends();
        setDiscoveredUsers((prev) => prev.filter((u) => u.id !== userId));
      } else {
        setDiscoveredUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, request_sent: true } : u))
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSendingRequest(null);
    }
  };

  const acceptRequest = async (requestId, fromUserId) => {
    setProcessingRequest(requestId);
    try {
      await axios.put(`${API}/friend-requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ami ajouté !");
      setFriendRequests((prev) => prev.filter((r) => r.id !== requestId));
      fetchFriends();
    } catch {
      toast.error("Erreur");
    } finally {
      setProcessingRequest(null);
    }
  };

  const declineRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      await axios.put(`${API}/friend-requests/${requestId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFriendRequests((prev) => prev.filter((r) => r.id !== requestId));
      toast.success("Demande refusée");
    } catch {
      toast.error("Erreur");
    } finally {
      setProcessingRequest(null);
    }
  };

  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      const res = await axios.post(`${API}/friends/invite`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = `${window.location.origin}/join/${res.data.id}`;
      setInviteLink(url);
      if (navigator.share) {
        try {
          await navigator.share({
            title: "Rejoins Gusto !",
            text: `${user.pseudo} t'invite à rejoindre Gusto, l'app cuisine entre amis !`,
            url,
          });
        } catch { /* cancelled */ }
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien d'invitation copié !");
      }
    } catch {
      toast.error("Erreur");
    } finally {
      setCreatingInvite(false);
    }
  };

  const removeFriend = async (friendId) => {
    try {
      await axios.delete(`${API}/friends/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFriends((prev) => prev.filter((f) => f.id !== friendId));
      toast.success("Ami supprimé");
    } catch {
      toast.error("Erreur");
    }
  };

  const getDietLabel = (profile) => {
    if (!profile) return null;
    const parts = [];
    if (profile.diet_type && profile.diet_type !== "omnivore") parts.push(profile.diet_type);
    if (profile.allergies?.length) parts.push(`${profile.allergies.length} allergie(s)`);
    return parts.length ? parts.join(" · ") : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="friends-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-charcoal">Mes Amis</h1>
            <p className="text-sm text-muted-foreground">{friends.length} ami{friends.length !== 1 ? "s" : ""}</p>
          </div>
          <Button onClick={() => navigate("/meals/new")} className="rounded-full gap-2" data-testid="plan-meal-btn">
            <CalendarPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Organiser un repas</span>
          </Button>
        </div>

        {/* Friend Requests Banner */}
        {friendRequests.length > 0 && (
          <Card className="border-blue-200 bg-blue-50/50" data-testid="friend-requests-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-500" />
                Demandes d'ami
                <Badge className="bg-blue-500 text-white">{friendRequests.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {friendRequests.map((req) => (
                <div key={req.id} className="flex items-center gap-3 p-2 rounded-xl bg-white">
                  <ChefAvatar src={req.from_user_avatar} name={req.from_user_pseudo} size="sm" showToque={false} />
                  <span className="flex-1 font-medium text-sm truncate">{req.from_user_pseudo}</span>
                  <div className="flex gap-1">
                    <Button
                      size="sm" variant="default"
                      className="rounded-full h-8 gap-1"
                      disabled={processingRequest === req.id}
                      onClick={() => acceptRequest(req.id, req.from_user_id)}
                      data-testid={`accept-request-${req.id}`}
                    >
                      <Check className="w-3 h-3" /> Accepter
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="rounded-full h-8"
                      disabled={processingRequest === req.id}
                      onClick={() => declineRequest(req.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="friends" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4 h-auto p-1">
            <TabsTrigger value="friends" className="gap-2">
              <Users className="w-4 h-4" /> Mes amis
            </TabsTrigger>
            <TabsTrigger value="discover" className="gap-2" data-testid="tab-discover">
              <Search className="w-4 h-4" /> Trouver des amis
            </TabsTrigger>
          </TabsList>

          {/* Friends Tab */}
          <TabsContent value="friends" className="space-y-4">
            {/* Invite Card */}
            <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-charcoal">Inviter un ami sur Gusto</p>
                    <p className="text-xs text-muted-foreground">Gagnez des points pour chaque parrainage !</p>
                  </div>
                </div>
                <Button onClick={createInvite} disabled={creatingInvite} className="w-full rounded-full gap-2" data-testid="create-invite-btn">
                  {creatingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  Créer un lien d'invitation
                </Button>
                {inviteLink && (
                  <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2">
                    <p className="text-xs text-muted-foreground truncate flex-1">{inviteLink}</p>
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Lien copié !"); }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Friends List */}
            {friends.length > 0 ? (
              <div className="space-y-3">
                {friends.map((friend, i) => {
                  const dietLabel = getDietLabel(friend.dietary_profile);
                  const allergies = friend.dietary_profile?.allergies || [];
                  return (
                    <motion.div key={friend.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <Link to={`/user/${friend.id}`} data-testid={`friend-avatar-${friend.id}`}>
                              <ChefAvatar src={friend.avatar} name={friend.pseudo} size="md" showToque={false} />
                            </Link>
                            <div className="flex-1 min-w-0">
                              <Link to={`/user/${friend.id}`} className="font-medium text-charcoal truncate hover:text-primary transition-colors block">
                                {friend.pseudo}
                              </Link>
                              {dietLabel && <p className="text-xs text-muted-foreground">{dietLabel}</p>}
                              {allergies.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {allergies.slice(0, 4).map((a) => (
                                    <Badge key={a} variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">
                                      <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> {a}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-500" onClick={() => removeFriend(friend.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground mb-4">Aucun ami pour le moment</p>
                  <Button onClick={createInvite} className="rounded-full gap-2">
                    <UserPlus className="w-4 h-4" /> Inviter un ami
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Discover Tab */}
          <TabsContent value="discover" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={searchQuery}
                onChange={(e) => searchUsers(e.target.value)}
                onFocus={() => { if (!searchQuery && discoveredUsers.length === 0) loadAllUsers(); }}
                className="pl-10 rounded-full"
                data-testid="search-users-input"
              />
            </div>

            {searchLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
              </div>
            ) : discoveredUsers.length > 0 ? (
              <div className="space-y-2">
                {discoveredUsers.map((u, i) => (
                  <motion.div key={u.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <Card>
                      <CardContent className="p-3 flex items-center gap-3">
                        <Link to={`/user/${u.id}`}>
                          <ChefAvatar src={u.avatar} name={u.pseudo} size="sm" showToque={false} />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-charcoal truncate">{u.pseudo}</p>
                          {u.total_points > 0 && (
                            <p className="text-xs text-muted-foreground">{u.total_points} pts</p>
                          )}
                        </div>
                        {u.request_sent ? (
                          <Badge variant="outline" className="gap-1 text-muted-foreground">
                            <Clock className="w-3 h-3" /> Envoyée
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            className="rounded-full gap-1 h-8"
                            disabled={sendingRequest === u.id}
                            onClick={() => sendFriendRequest(u.id)}
                            data-testid={`add-friend-${u.id}`}
                          >
                            {sendingRequest === u.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <UserPlus className="w-3 h-3" />
                            )}
                            Ajouter
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : searchQuery.length >= 2 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground text-sm">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Search className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground text-sm">Recherchez un pseudo pour trouver des amis</p>
                <Button variant="outline" className="mt-3 rounded-full" onClick={loadAllUsers}>
                  Voir tous les utilisateurs
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FriendsPage;
